import { useState, useEffect, useCallback, useRef } from "react";

const CHARS = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%&*";

/**
 * TextScramble — reveals text with a cryptographic scramble effect.
 * Props:
 *   text: string — target text
 *   className: string
 *   speed: number — ms per character reveal (default 30)
 *   trigger: boolean — when true, starts the animation
 */
export default function TextScramble({
  text = "",
  className = "",
  speed = 30,
  trigger = true,
}) {
  const [displayed, setDisplayed] = useState("");
  const frameRef = useRef(null);
  const indexRef = useRef(0);

  const scramble = useCallback(() => {
    if (!trigger || !text) return;
    indexRef.current = 0;
    setDisplayed("");

    const tick = () => {
      const i = indexRef.current;
      if (i >= text.length) {
        setDisplayed(text);
        return;
      }

      const revealed = text.slice(0, i);
      const scrambled = Array.from({ length: Math.min(4, text.length - i) }, () =>
        CHARS[Math.floor(Math.random() * CHARS.length)]
      ).join("");

      setDisplayed(revealed + scrambled);
      indexRef.current = i + 1;
      frameRef.current = setTimeout(tick, speed);
    };

    // slight initial delay for dramatic effect
    frameRef.current = setTimeout(tick, 200);
  }, [text, speed, trigger]);

  useEffect(() => {
    // Respect reduced motion
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) {
      setDisplayed(text);
      return;
    }

    scramble();
    return () => clearTimeout(frameRef.current);
  }, [scramble, text]);

  return (
    <span className={className} aria-label={text}>
      {displayed}
    </span>
  );
}
